<template>
    <div class="user-config">
        <!-- 主色调 -->
        <div class="form-section">
            <h4 class="section-title">
                <i class="el-icon-sunny"></i>
                {{ $t('userConfig.themeAccent') }}
            </h4>
            <div class="setting-item theme-accent-item">
                <div class="setting-label">
                    <i class="el-icon-s-opportunity"></i>
                    <span>{{ $t('userConfig.themeAccentDesc') }}</span>
                </div>
                <div class="setting-content accent-list">
                    <div v-for="item in themeAccents" :key="item.id" class="accent-item"
                        :class="{ active: primaryColor === item.color }" :style="{ '--accent': item.color }"
                        :title="$t(item.nameKey)" @click="onThemeAccentChange(item.color)">
                        <i v-if="primaryColor === item.color" class="el-icon-check"></i>
                    </div>
                    <el-color-picker v-model="primaryColor" size="mini" :predefine="predefineColors"
                        class="accent-picker" :class="{ active: isCustomAccent }"
                        :title="$t('userConfig.themeAccentCustom')" @change="onThemeAccentChange" />
                </div>
            </div>
        </div>

        <!-- 语言设置 -->
        <div class="form-section">
            <h4 class="section-title">
                <i class="el-icon-s-operation"></i>
                {{ $t('setting.language') }}
            </h4>
            <div class="setting-item">
                <div class="setting-label">
                    <i class="el-icon-edit-outline"></i>
                    <span>{{ $t('setting.languageDesc') }}</span>
                </div>
                <div class="setting-content">
                    <el-select v-model="currentLocale" @change="onLocaleChange" size="small" style="width: 140px">
                        <el-option label="简体中文" value="zh"> </el-option>
                        <el-option label="English" value="en"> </el-option>
                    </el-select>
                </div>
            </div>
        </div>

        <!-- 隐私设置 -->
        <div class="form-section">
            <h4 class="section-title">
                <i class="el-icon-lock"></i>
                {{ $t('userConfig.privacy') }}
            </h4>
            <div class="setting-item">
                <div class="setting-label">
                    <i class="el-icon-user-solid"></i>
                    <span>{{ $t('userConfig.manualApprove') }}</span>
                </div>
                <div class="setting-content">
                    <el-switch v-model="userInfo.isManualApprove" @change="onManualApproveChange"></el-switch>
                </div>
            </div>
        </div>

        <!-- 通知设置 -->
        <div class="form-section">
            <h4 class="section-title">
                <i class="el-icon-bell"></i>
                {{ $t('userConfig.notification') }}
            </h4>
            <div class="setting-item">
                <div class="setting-label">
                    <i class="el-icon-microphone"></i>
                    <span>{{ $t('userConfig.audioTip') }}</span>
                </div>
                <div class="setting-content">
                    <el-switch v-model="userInfo.isAudioTip" @change="onMessageAudioTip"></el-switch>
                </div>
            </div>
        </div>

        <!-- 聊天背景 -->
        <div class="form-section">
            <h4 class="section-title">
                <i class="el-icon-s-grid"></i>
                {{ $t('userConfig.chatBg') }}
            </h4>
            <div class="chat-bg-list">
                <div v-for="item in bgPresets" :key="item.id" class="chat-bg-item"
                    :class="['preview-' + item.id, { active: currentChatBg === item.id }]"
                    @click="onSelectChatBg(item.id)">
                    <span class="chat-bg-name">{{ $t(item.nameKey) }}</span>
                    <i v-if="currentChatBg === item.id" class="el-icon-check check-icon"></i>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { setLocale } from '@/i18n';
import { chatBgPresets } from '@/api/chatBg';
import { themeAccents, themeDefaultColor } from '@/api/theme';

export default {
    name: "userConfig",
    data() {
        return {
            currentLocale: 'zh',
            currentChatBg: 'default',
            primaryColor: themeDefaultColor,
            themeAccents,
            predefineColors: themeAccents.map((a) => a.color),
            bgPresets: chatBgPresets,
            userInfo: {
                isManualApprove: false,
                isAudioTip: false
            }
        }
    },
    computed: {
        isCustomAccent() {
            return !themeAccents.some((a) => a.color === this.primaryColor);
        }
    },
    methods: {
        init() {
            let mine = this.userStore.userInfo;
            this.userInfo = JSON.parse(JSON.stringify(mine));
            this.currentLocale = this.$i18n.locale;
            this.currentChatBg = this.configStore.chatBg;
            this.primaryColor = this.configStore.themeAccent;
        },
        onLocaleChange(locale) {
            setLocale(locale);
        },
        onThemeAccentChange(color) {
            if (!color) return;
            this.primaryColor = this.configStore.setThemeAccent(color);
        },
        onSelectChatBg(id) {
            this.configStore.setChatBg(id);
            this.currentChatBg = id;
        },
        onManualApproveChange() {
            this.$http({
                url: "/user/manualApprove?enabled=" + this.userInfo.isManualApprove,
                method: 'put'
            }).then(() => {
                this.userStore.loadUser();
            })
        },
        onMessageAudioTip() {
            this.$http({
                url: "/user/audioTip?enabled=" + this.userInfo.isAudioTip,
                method: 'put'
            }).then(() => {
                this.userStore.loadUser();
            })
        }
    }
}
</script>
<style scoped lang="scss">
.user-config {
    padding: 15px;
    background: #fafbfc;
    min-height: 400px;

    // 表单区域
    .form-section {
        background: white;
        border-radius: 8px;
        padding: 15px;
        margin-bottom: 12px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        border: 1px solid #f0f0f0;

        .section-title {
            margin: 0 0 15px 0;
            font-size: var(--im-font-size-larger);
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 6px;
            padding-bottom: 8px;
            border-bottom: 1px solid #f5f5f5;

            i {
                color: var(--im-color-primary);
                font-size: 16px;
            }
        }
    }

    .chat-bg-list {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 10px;

        .chat-bg-item {
            position: relative;
            height: 72px;
            border-radius: 8px;
            border: 2px solid transparent;
            cursor: pointer;
            overflow: hidden;
            box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.06);
            transition: border-color 0.2s, transform 0.15s;

            &:hover {
                transform: translateY(-1px);
            }

            &.active {
                border-color: var(--im-color-primary);
            }

            .chat-bg-name {
                position: absolute;
                left: 8px;
                bottom: 6px;
                font-size: 12px;
                color: #445;
                background: rgba(255, 255, 255, 0.72);
                padding: 1px 6px;
                border-radius: 4px;
            }

            .check-icon {
                position: absolute;
                top: 6px;
                right: 6px;
                width: 18px;
                height: 18px;
                border-radius: 50%;
                background: var(--im-color-primary);
                color: #fff;
                font-size: 12px;
                display: flex;
                align-items: center;
                justify-content: center;
            }

            &.preview-default {
                background: #f6f7f8;
            }

            &.preview-sky {
                background: linear-gradient(160deg, #eaf4ff 0%, #f4f9ff 45%, #eef6fc 100%);
            }

            &.preview-lavender {
                background: linear-gradient(160deg, #f1edff 0%, #f7f5ff 45%, #efeafc 100%);
            }

            &.preview-warm {
                background: linear-gradient(160deg, #fbf5e9 0%, #fdf9f1 50%, #f7f0e4 100%);
            }

            &.preview-dots {
                background-color: #f5f7fb;
                background-image: radial-gradient(rgba(100, 120, 160, 0.18) 1.2px, transparent 1.2px);
                background-size: 16px 16px;
            }

            &.preview-grid {
                background-color: #f6f7f9;
                background-image:
                    linear-gradient(rgba(90, 110, 150, 0.08) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(90, 110, 150, 0.08) 1px, transparent 1px);
                background-size: 20px 20px;
            }

            &.preview-stripe {
                background-color: #f7f8fb;
                background-image: repeating-linear-gradient(
                    -45deg,
                    transparent,
                    transparent 8px,
                    rgba(90, 110, 150, 0.07) 8px,
                    rgba(90, 110, 150, 0.07) 9px
                );
            }

            &.preview-diamond {
                background-color: #f8f9fb;
                background-image:
                    linear-gradient(45deg, transparent 48.5%, rgba(90, 110, 150, 0.055) 48.5%, rgba(90, 110, 150, 0.055) 51.5%, transparent 51.5%),
                    linear-gradient(-45deg, transparent 48.5%, rgba(90, 110, 150, 0.055) 48.5%, rgba(90, 110, 150, 0.055) 51.5%, transparent 51.5%);
                background-size: 36px 36px;
            }
        }
    }

    // 设置项
    .setting-item {
        display: flex;
        align-items: center;
        padding: 12px 0;
        border-bottom: 1px solid #f5f5f5;

        &:last-child {
            border-bottom: none;
        }

        .setting-label {
            flex: 1;
            display: flex;
            align-items: center;
            gap: 8px;
            width: 100px;
            font-weight: 500;
            color: var(--im-text-color-light);
            font-size: var(--im-font-size);

            i {
                color: var(--im-color-primary);
                font-size: var(--im-font-size-large);
            }
        }

        .setting-content {
            flex: 1;
            display: flex;
            justify-content: flex-end;
        }

        &.theme-accent-item {
            align-items: flex-start;

            .accent-list {
                display: flex;
                flex-wrap: wrap;
                gap: 10px;
                justify-content: flex-end;
                max-width: 280px;
            }

            .accent-item {
                width: 28px;
                height: 28px;
                border-radius: 50%;
                background: var(--accent);
                cursor: pointer;
                border: 2px solid transparent;
                box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
                display: flex;
                align-items: center;
                justify-content: center;
                color: #fff;
                font-size: 14px;
                transition: transform 0.15s, border-color 0.15s;

                &:hover {
                    transform: scale(1.08);
                }

                &.active {
                    border-color: #333;
                    box-shadow: 0 0 0 2px #fff, 0 0 0 4px var(--accent);
                }
            }

            .accent-picker {
                height: 28px;

                ::v-deep .el-color-picker__trigger {
                    width: 28px;
                    height: 28px;
                    padding: 2px;
                    border-radius: 50%;
                    border: 2px solid transparent;
                    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
                }

                ::v-deep .el-color-picker__color {
                    border-radius: 50%;
                    border: none;
                }

                ::v-deep .el-color-picker__color-inner {
                    border-radius: 50%;
                }

                ::v-deep .el-color-picker__icon {
                    display: none;
                }
            }
        }
    }
}
</style>
